import { ThreeEvent, useLoader } from '@react-three/fiber';
import { useEffect, useMemo, useState } from 'react';
import * as THREE from 'three';
import type { DesignSpec, SurfaceImage } from '@shared/spec';

export type TopSurfaceBounds = {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  topZ: number;
  width: number;
  depth: number;
};

type DragState = {
  id: string;
  offset: [number, number];
};

type SurfaceImageLayerProps = {
  spec?: DesignSpec;
  imageUrls: Record<string, string>;
  topBounds: TopSurfaceBounds | null;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onChange: (image: SurfaceImage, commit: boolean) => void;
};

export function SurfaceImageLayer({
  spec,
  imageUrls,
  topBounds,
  selectedId,
  onSelect,
  onChange,
}: SurfaceImageLayerProps) {
  const [drag, setDrag] = useState<DragState | null>(null);

  const surfaceImages = useMemo(
    () =>
      [...(spec?.surfaceImages ?? [])]
        .filter((image) => image.target.kind === 'object_top')
        .sort((a, b) => a.renderOrder - b.renderOrder),
    [spec?.surfaceImages],
  );

  if (!topBounds || surfaceImages.length === 0) return null;

  const pointToTopPlane = (point: THREE.Vector3): [number, number] => [
    point.x,
    -point.z,
  ];

  const updateDraggedImage = (point: THREE.Vector3, commit: boolean) => {
    if (!drag) return;
    const image = surfaceImages.find((candidate) => candidate.id === drag.id);
    if (!image || image.locked) return;

    const [x, y] = pointToTopPlane(point);
    const next = clampImageToTop(
      {
        ...image,
        center: [x + drag.offset[0], y + drag.offset[1]],
      },
      topBounds,
    );
    onChange(next, commit);
  };

  return (
    <>
      <mesh
        position={[0, topBounds.topZ + 0.6, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
        onPointerMove={(event) => updateDraggedImage(event.point, false)}
        onPointerUp={(event) => {
          updateDraggedImage(event.point, true);
          setDrag(null);
        }}
        onPointerLeave={(event) => {
          updateDraggedImage(event.point, true);
          setDrag(null);
        }}
      >
        <planeGeometry args={[topBounds.width * 1.2, topBounds.depth * 1.2]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>

      {surfaceImages.map((image) => {
        const url = imageUrls[image.assetId];
        if (!url) return null;
        return (
          <SurfaceImagePlane
            key={image.id}
            image={image}
            url={url}
            topZ={topBounds.topZ}
            selected={selectedId === image.id}
            onPointerDown={(event) => {
              event.stopPropagation();
              if (image.locked) return;
              const [x, y] = pointToTopPlane(event.point);
              onSelect(image.id);
              setDrag({
                id: image.id,
                offset: [image.center[0] - x, image.center[1] - y],
              });
            }}
            onPointerMove={(event) => {
              if (drag?.id !== image.id) return;
              event.stopPropagation();
              updateDraggedImage(event.point, false);
            }}
            onPointerUp={(event) => {
              if (drag?.id !== image.id) return;
              event.stopPropagation();
              updateDraggedImage(event.point, true);
              setDrag(null);
            }}
          />
        );
      })}
    </>
  );
}

function SurfaceImagePlane({
  image,
  url,
  topZ,
  selected,
  onPointerDown,
  onPointerMove,
  onPointerUp,
}: {
  image: SurfaceImage;
  url: string;
  topZ: number;
  selected: boolean;
  onPointerDown: (event: ThreeEvent<PointerEvent>) => void;
  onPointerMove: (event: ThreeEvent<PointerEvent>) => void;
  onPointerUp: (event: ThreeEvent<PointerEvent>) => void;
}) {
  const texture = useLoader(THREE.TextureLoader, url);

  useEffect(() => {
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = 8;
    texture.needsUpdate = true;
  }, [texture]);

  const rotation = -THREE.MathUtils.degToRad(image.rotationDeg);
  const [width, height] = image.size;

  return (
    <group
      position={[image.center[0], topZ + 0.8 + image.renderOrder * 0.05, -image.center[1]]}
      rotation={[0, rotation, 0]}
      renderOrder={1000 + image.renderOrder}
    >
      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        <planeGeometry args={[width, height]} />
        <meshBasicMaterial
          map={texture}
          transparent
          opacity={image.opacity}
          side={THREE.DoubleSide}
          depthWrite={false}
          polygonOffset
          polygonOffsetFactor={-4}
        />
      </mesh>
      {selected && (
        <mesh rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[width * 1.01, height * 1.01]} />
          <meshBasicMaterial
            color="#2B4C7E"
            wireframe
            transparent
            opacity={0.85}
            depthWrite={false}
          />
        </mesh>
      )}
    </group>
  );
}

function clampImageToTop(
  image: SurfaceImage,
  bounds: TopSurfaceBounds,
): SurfaceImage {
  const halfW = image.size[0] / 2;
  const halfH = image.size[1] / 2;
  const minX = bounds.minX + Math.min(halfW, bounds.width / 2);
  const maxX = bounds.maxX - Math.min(halfW, bounds.width / 2);
  const minY = bounds.minY + Math.min(halfH, bounds.depth / 2);
  const maxY = bounds.maxY - Math.min(halfH, bounds.depth / 2);

  return {
    ...image,
    center: [
      THREE.MathUtils.clamp(image.center[0], Math.min(minX, maxX), Math.max(minX, maxX)),
      THREE.MathUtils.clamp(image.center[1], Math.min(minY, maxY), Math.max(minY, maxY)),
    ],
  };
}
